import { useMemo } from 'react';
import storage from 'utils/storage';
import { FIRST_VISIT_TIMESTAMP_KEY } from 'constants/cookies';

/**
 * useFirstVisit - Determines if user is a first-time visitor
 * 
 * Logic:
 * - First visit: No timestamp exists → return true, set timestamp
 * - Return visit: Timestamp exists and < 30 days old → return false
 * - New first visit: Timestamp > 30 days old → return true, reset timestamp
 * 
 * Used for:
 * - Sticky footer primary button text ("Request More Info" vs "Book a Showing")
 * - PVR modal experiments
 * 
 * @param isExperimentEnabled - Whether the experiment is active (optional)
 * @returns boolean - true if first visit, false if return visit
 */
export function useFirstVisit(isExperimentEnabled = true): boolean {
  return useMemo(() => {
    const firstVisitTimestamp = storage.get(FIRST_VISIT_TIMESTAMP_KEY) as number;
    
    if (!firstVisitTimestamp) {
      // No timestamp exists - first visit
      storage.set(FIRST_VISIT_TIMESTAMP_KEY, Date.now());
      return true;
    }
    
    const currentDate = Date.now();
    const timeDifference = currentDate - firstVisitTimestamp;
    const timeDifferenceDays = timeDifference / (1000 * 60 * 60 * 24);
    
    // If experiment enabled and within 30 days (but > 1 second to avoid same-page reloads)
    if (isExperimentEnabled && timeDifferenceDays < 30 && timeDifference > 1000) {
      // Return visit within 30 days
      storage.set(FIRST_VISIT_TIMESTAMP_KEY, Date.now());
      return false;
    }
    
    // More than 30 days - treat as new first visit
    return true;
  }, [isExperimentEnabled]);
}
